import { createFileRoute, Link } from "@tanstack/react-router";
import { StaticPage, staticHead } from "@/components/StaticPage";

export const Route = createFileRoute("/methodology")({
  component: MethodologyPage,
  head: () =>
    staticHead({
      title: "Data Methodology & Sources | AutoSpecData",
      description:
        "Where AutoSpecData data comes from: EPA fuel economy records, NHTSA vPIC and computed maintenance intervals, plus refresh cadence and limits.",
      path: "/methodology",
    }),
});

function MethodologyPage() {
  return (
    <StaticPage
      title="Data Methodology & Sources"
      intro="Every figure on AutoSpecData traces back to a named source. This page documents which source covers which data type, how values are derived, and where the limits are."
    >
      <section>
        <h2>Primary sources</h2>
        <ul>
          <li>
            <strong>EPA / fueleconomy.gov</strong> — city, highway, and combined MPG,
            annual fuel cost, CO2 tailpipe grams per mile, fuel economy and greenhouse gas
            scores, engine cylinders and displacement, transmission, drivetrain, fuel type,
            and EPA vehicle class. These are official test results published by the U.S.
            Environmental Protection Agency and the Department of Energy.
          </li>
          <li>
            <strong>NHTSA vPIC</strong> — the make and model universe used to structure the
            site.
          </li>
          <li>
            <strong>Manufacturer documentation</strong> — towing capacity, tire size, and
            wheel fitment. These pages publish only where a verified figure exists; we do
            not estimate them.
          </li>
        </ul>
      </section>
      <section>
        <h2>Computed values</h2>
        <p>
          Maintenance intervals are computed, not copied. Each schedule is derived from the
          vehicle's recorded powertrain: fuel type, engine configuration, transmission type,
          drivetrain, and model year. An electric vehicle gets no oil service, a CVT gets a
          shorter fluid interval than a conventional automatic, all-wheel drive adds
          differential and transfer-case service, and vehicles from the synthetic-oil era
          get longer oil intervals than earlier ones. These are general intervals: your
          owner's manual is the authority for your specific vehicle, and severe-duty use
          shortens most of them.
        </p>
        <p>
          Comparison and ranking pages are computed from the same underlying records — no
          separate dataset, no editorial reordering.
        </p>
      </section>
      <section>
        <h2>Freshness and verification</h2>
        <p>
          Each record stores the date it was last verified against its source, shown on the
          page as a "verified" badge. Records are re-checked when the source publishes new
          model-year data.
        </p>
      </section>
      <section>
        <h2>Known limits</h2>
        <ul>
          <li>
            EPA figures are laboratory test results. Real-world economy varies with driving
            style, load, terrain, and climate.
          </li>
          <li>
            Coverage follows what the EPA publishes. Some trims, commercial vehicles, and
            heavy-duty models are not in the federal dataset.
          </li>
          <li>
            Where a value is missing, the page shows it as unavailable rather than filling
            it with an estimate.
          </li>
        </ul>
      </section>
      <section>
        <h2>Corrections</h2>
        <p>
          Send corrections through the <Link to="/contact">contact page</Link>. Include the
          year, trim, the value you believe is wrong, and the document you are comparing
          against.
        </p>
      </section>
    </StaticPage>
  );
}
